import React, { useState, useMemo } from "react";
import { IReducer } from "../../state/reducer";
import ActionButton from "../ActionButton/ActionButton";
import DropdownSelect from "../FilterSelect/FilterSelect";
import PrimaryFilter from "../PrimaryFilter/PrimaryFilter";
import { ActionEntry } from "../../state/BrickActions/ActionEntries";
import "./Actions.css";
import ActionRow from "../ActionRow/ActionRow";
import { RowMode } from "../RowMode/RowMode";
import HelpIcon from "../HelpIcon/HelpIcon";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

interface ActionProps extends IReducer {
  showActionSettings: CallableFunction;
}

const Actions: React.FC<ActionProps> = ({
  state,
  dispatch,
  showActionSettings,
}) => {
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [activeSearch, setActiveSearch] = useState<string>("");

  // Calculate total visible actions (before search/filters)
  const totalVisibleActions = useMemo(() => {
    let count = 0;
    state.actions.forEach((actionEntry) => {
      if (
        actionEntry &&
        actionEntry.visible &&
        actionEntry.isBrickpack == false &&
        actionEntry.type !== "BuySet"
      ) {
        count++;
      }
    });
    return count;
  }, [state.actions]);

  // Memoized computation of visible actions with custom ordering
  const visibleActions = useMemo(() => {
    let filteredActions: ActionEntry[] = [];

    state.actions.forEach((actionEntry, key) => {
      if (
        actionEntry &&
        actionEntry.visible &&
        !actionEntry.userHidden &&
        actionEntry.isBrickpack == false &&
        actionEntry.type !== "BuySet" && // Exclude BuySet actions (they appear in Sets module)
        !Number.isNaN(key)
      ) {
        filteredActions.push(actionEntry);
      }
    });

    // Apply search filter
    if (activeSearch.trim() !== "") {
      filteredActions = filteredActions.filter((action) =>
        (action.type.toLowerCase() + " " + action.title.toLowerCase()).includes(
          activeSearch.toLowerCase(),
        ),
      );
    }

    // Apply type filters
    if (appliedFilters.length > 0) {
      filteredActions = filteredActions.filter((action) =>
        appliedFilters.includes(action.type),
      );
    }

    // Apply custom ordering if actionOrder is not empty
    if (state.actionOrder.length > 0) {
      const ordered: ActionEntry[] = [];
      const remaining = new Set(filteredActions.map((a) => a.id));

      // Add actions in custom order
      state.actionOrder.forEach((id) => {
        const action = filteredActions.find((a) => a.id === id);
        if (action) {
          ordered.push(action);
          remaining.delete(id);
        }
      });

      // Append new actions not in order (e.g., newly revealed)
      filteredActions.forEach((action) => {
        if (remaining.has(action.id)) {
          ordered.push(action);
        }
      });

      return ordered;
    }

    return filteredActions;
  }, [state.actions, state.actionOrder, appliedFilters, activeSearch]);

  // Memoized filter options (if needed)
  const filterOptions = useMemo(
    () => ["Auto Collect", "Create", "Split", "Capacity", "Upgrade", "Collect"],
    [],
  );

  // Drag and drop sensors (only activate when editMode is active)
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts (prevents accidental drags)
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms delay for touch (prevents accidental drags on scroll)
        tolerance: 5,
      },
    }),
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    if (!state.editModeActive) return; // Only allow reorder in Edit mode

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = visibleActions.findIndex((a) => a.id === active.id);
    const newIndex = visibleActions.findIndex((a) => a.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(visibleActions, oldIndex, newIndex);
    const newOrder = reordered.map((a) => a.id);

    dispatch({ type: "SET_ACTION_ORDER", actionOrder: newOrder });
  };

  return (
    <div className="moduleContainer">
      <div className="moduleSpacer"></div>
      <div className="inventoryHeader">
        <div className="headerContent">
          <div className="inventoryHeaderTitle">
            {state.editModeActive ? "Reorder Actions" : "Actions"}
          </div>
        </div>
      </div>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={visibleActions.map((a) => a.id)}
          strategy={
            state.useActionRows
              ? verticalListSortingStrategy
              : rectSortingStrategy
          }
        >
          {state.useActionRows ? (
            <div className="rowActions">
              {visibleActions.map((action) => (
                <ActionRow
                  key={action.id}
                  id={action.id}
                  title={action.title}
                  type={action.type}
                  details={action.details}
                  state={state}
                  dispatch={dispatch}
                />
              ))}
            </div>
          ) : (
            <div className="actions">
              {visibleActions.map((action) => (
                <ActionButton
                  key={action.id}
                  id={action.id}
                  title={action.title}
                  type={action.type}
                  details={action.details}
                  state={state}
                  dispatch={dispatch}
                />
              ))}
            </div>
          )}
        </SortableContext>
      </DndContext>
      <div
        className={`inventoryFooter ${state.editModeActive ? "editModeActive" : ""}`}
      >
        <div className="inventoryHeaderOptions">
          <div className="compendiumFilters">
            <div className="moduleTotal">
              Total:{" "}
              <span className="moduleTotalAmt">{totalVisibleActions}</span>
            </div>
            <DropdownSelect
              placeholder="Filters"
              options={filterOptions}
              setAppliedFilters={setAppliedFilters}
              disabled={state.editModeActive}
            />
            <PrimaryFilter
              placeholder="Search"
              setActiveSearch={setActiveSearch}
              disabled={state.editModeActive}
            />
            <RowMode
              state={state}
              dispatch={dispatch}
              rowType="TOGGLE_USE_ACTION_ROWS"
              disabled={state.editModeActive}
            />
            <div
              className={`actionEditMode ${state.editModeActive ? "active" : ""}`}
              onClick={() => dispatch({ type: "TOGGLE_EDIT_MODE" })}
            >
              {state.editModeActive ? "Save" : "Edit"}
            </div>
            <div
              className={`actionSettings ${state.editModeActive ? "disabled" : ""}`}
              onClick={() => !state.editModeActive && showActionSettings(true)}
            >
              Settings
            </div>
          </div>
        </div>
      </div>
      <div className="moduleSpacer"></div>
    </div>
  );
};

export default Actions;
